import type {
  LngLat,
  Shape,
  ShapeImage,
  ShapeStyle,
  ShapeText,
  ShapeUrlAction,
} from "../adapters/types";

export type ShapeStoreListener = (shapes: Shape[]) => void;

/** Détient les formes en mémoire. Agnostique de toute lib cartographique. */
export class ShapeStore {
  private shapes = new Map<string, Shape>();
  private listeners = new Set<ShapeStoreListener>();

  /**
   * Triées par `layer` croissant (voir `Shape.layer`), puis par `order` (voir `Shape.order`) pour
   * départager deux formes du même calque — jamais par ordre d'insertion dans la Map interne :
   * sans ce tri explicite, retirer puis réinsérer une forme (ex. undo d'une suppression) la ferait
   * réapparaître en dernière position (globalement, ou au sein de son propre calque si on ne triait
   * que par `layer`), donc visuellement au-dessus de ses voisines, peu importe sa position d'origine.
   * Une forme sans `layer` (ex. dessinée à la main) s'affiche au-dessus de toutes les formes calquées.
   */
  getAll(): Shape[] {
    return Array.from(this.shapes.values()).sort((a, b) => {
      const layerA = a.layer ?? Number.POSITIVE_INFINITY;
      const layerB = b.layer ?? Number.POSITIVE_INFINITY;
      if (layerA !== layerB) return layerA - layerB;

      const orderA = a.order ?? Number.POSITIVE_INFINITY;
      const orderB = b.order ?? Number.POSITIVE_INFINITY;
      return orderA - orderB;
    });
  }

  add(shape: Shape): void {
    this.shapes.set(shape.id, shape);
    this.notify();
  }

  /** Insère plusieurs formes en une seule notification (ex. chargement initial). */
  addMany(shapes: Shape[]): void {
    for (const shape of shapes) this.shapes.set(shape.id, shape);
    this.notify();
  }

  /**
   * Remplace TOUT le contenu du store en une seule notification — les formes déjà présentes qui
   * ne sont pas dans `shapes` disparaissent, contrairement à `addMany` qui ne fait qu'ajouter.
   * Sert à changer de document sans reconstruire l'instance (voir `TokaMapInstance.loadDocument`) :
   * en deux temps (vider puis `addMany`), le rendu intermédiaire verrait une carte vide.
   */
  replaceAll(shapes: Shape[]): void {
    this.shapes.clear();
    for (const shape of shapes) this.shapes.set(shape.id, shape);
    this.notify();
  }

  remove(shapeId: string): void {
    this.shapes.delete(shapeId);
    this.notify();
  }

  moveVertex(shapeId: string, vertexIndex: number, position: LngLat): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;

    const coordinates = [...shape.coordinates];
    coordinates[vertexIndex] = position;
    // Le sommet 0 est dupliqué en fin d'anneau pour le fermer : on les garde synchronisés — SAUF
    // pour un cercle (2 points `[centre, point de bord]`) ni pour une ligne (polyligne OUVERTE,
    // voir `Shape.coordinates`) : dans les deux cas, le dernier index désigne un point RÉEL
    // distinct, pas un doublon du premier, donc rien à synchroniser.
    if (vertexIndex === 0 && shape.shapeKind !== "circle" && shape.shapeKind !== "line") {
      coordinates[coordinates.length - 1] = position;
    }

    this.shapes.set(shapeId, { ...shape, coordinates });
    this.notify();
  }

  rotateShape(shapeId: string, rotation: number, coordinates: LngLat[]): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, rotation, coordinates });
    this.notify();
  }

  /** Translation de la forme entière (tous les sommets) — rotation inchangée. */
  moveShape(shapeId: string, coordinates: LngLat[]): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, coordinates });
    this.notify();
  }

  /** Remplace `shapeKind`/`coordinates`/`style` en bloc — voir `ConvertShapeKindCommand` (conversion
   * polygone <-> ligne, "Couper"/rejoindre les 2 extrémités d'une ligne). Absolu (aucun delta à
   * composer avec une autre édition entre-temps), même convention que `moveShape`/`rotateShape`. */
  setShapeKindAndCoordinates(
    shapeId: string,
    patch: { shapeKind: Shape["shapeKind"]; coordinates: LngLat[]; style: ShapeStyle | undefined },
  ): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, ...patch });
    this.notify();
  }

  /** Change le calque (`Shape.layer`, z-order) d'une forme — voir `getAll` pour l'ordre de tri. */
  setShapeLayer(shapeId: string, layer: number | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, layer });
    this.notify();
  }

  /** Remplace le style (`Shape.style` : bgColor/borderColor) d'une forme — voir `SetShapeStyleCommand`. */
  setShapeStyle(shapeId: string, style: ShapeStyle | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, style });
    this.notify();
  }

  /** Remplace le style de survol (`Shape.hoverStyle`) d'une forme — voir
   * `SetShapeHoverStyleCommand`. */
  setShapeHoverStyle(shapeId: string, hoverStyle: ShapeStyle | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, hoverStyle });
    this.notify();
  }

  /** Remplace l'image (`Shape.image`) d'une forme — voir `SetShapeImageCommand`. */
  setShapeImage(shapeId: string, image: ShapeImage | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, image });
    this.notify();
  }

  /** Remplace l'action (`Shape.action`) d'une forme — voir `SetShapeActionCommand`. */
  setShapeAction(shapeId: string, action: string | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, action });
    this.notify();
  }

  /** Remplace `Shape.useActionIcon` d'une forme — voir `SetShapeUseActionIconCommand`. */
  setShapeUseActionIcon(shapeId: string, useActionIcon: boolean | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, useActionIcon });
    this.notify();
  }

  /** Remplace `Shape.locked` d'une forme — voir `SetShapeLockedCommand`. */
  setShapeLocked(shapeId: string, locked: boolean | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, locked });
    this.notify();
  }

  /** Remplace `Shape.walkable` d'une forme — voir `SetShapeWalkableCommand`. */
  setShapeWalkable(shapeId: string, walkable: boolean | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, walkable });
    this.notify();
  }

  /** Remplace `Shape.themeId` d'une forme — voir `SetShapeThemeIdCommand`. */
  setShapeThemeId(shapeId: string, themeId: string | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, themeId });
    this.notify();
  }

  /** Remplace `Shape.pathPriority` d'une forme — voir `SetShapePathPriorityCommand`. */
  setShapePathPriority(shapeId: string, pathPriority: Shape["pathPriority"]): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, pathPriority });
    this.notify();
  }

  setShapeHeight(shapeId: string, height: Shape["height"]): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, height });
    this.notify();
  }

  setShapeElevation(shapeId: string, elevation: Shape["elevation"]): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, elevation });
    this.notify();
  }

  setShapeHoverHeight(shapeId: string, hoverHeight: Shape["hoverHeight"]): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, hoverHeight });
    this.notify();
  }

  setShapeHoverElevation(shapeId: string, hoverElevation: Shape["hoverElevation"]): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, hoverElevation });
    this.notify();
  }

  setShapeMinZoomOffset(shapeId: string, minZoomOffset: Shape["minZoomOffset"]): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, minZoomOffset });
    this.notify();
  }

  /** Remplace la charge métier opaque (`Shape.entity`) d'une forme — voir `SetShapeEntityCommand`. */
  setShapeEntity(shapeId: string, entity: unknown): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, entity });
    this.notify();
  }

  /** Remplace la configuration de redirection (`Shape.urlAction`) d'une forme — voir `SetShapeUrlActionCommand`. */
  setShapeUrlAction(shapeId: string, urlAction: ShapeUrlAction | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, urlAction });
    this.notify();
  }

  /** Remplace le document ouvert au clic (`Shape.targetDocumentId`) d'une forme — voir `SetShapeTargetDocumentIdCommand`. */
  setShapeTargetDocumentId(shapeId: string, targetDocumentId: string | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, targetDocumentId });
    this.notify();
  }

  /** Remplace le texte (`Shape.text`) d'une forme — voir `SetShapeTextCommand`. */
  setShapeText(shapeId: string, text: ShapeText | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, text });
    this.notify();
  }

  /** Remplace le texte de survol (`Shape.hoverText`) d'une forme — voir
   * `SetShapeHoverTextCommand`. */
  setShapeHoverText(shapeId: string, hoverText: ShapeText | undefined): void {
    const shape = this.shapes.get(shapeId);
    if (!shape) return;
    this.shapes.set(shapeId, { ...shape, hoverText });
    this.notify();
  }

  onChange(listener: ShapeStoreListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const shapes = this.getAll();
    for (const listener of this.listeners) listener(shapes);
  }
}
